import React, { useState, useEffect } from 'react'
import {
  Form,
  Input,
  Button,
  Table,
  Space,
  Select,
  Col,
  Row,
  Modal,
} from 'antd'
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import AdminHeader from '../../CommonComponents/AdminHeader'
import AdminFooter from '../../CommonComponents/AdminFooter'
import {
  getCast,
  getSubCast,
  saveSubCast,
  deleteSubCast,
  updateSubCast,
} from '../../APIComponents/AdminApi'
import { toast } from 'react-toastify'
import axiosInstance from '../../../Utils/axiosInstance'

const SubCastMaster = () => {
  //===============================================
  const loginType = sessionStorage.getItem('loginType') // 'ADMIN' or 'USER'
  const menuData = JSON.parse(sessionStorage.getItem('menus')) || []

  const getPagePermission = () => {
    if (loginType === 'ADMIN') {
      return {
        is_new: 1,
        is_edit: 1,
        is_view: 1,
        is_delete: 1,
      }
    }

    for (const menu of menuData) {
      const page = menu.pages?.find((p) => p.page_name === 'Sub Cast Master')
      if (page) return page
    }

    return {
      is_new: 0,
      is_edit: 0,
      is_view: 0,
      is_delete: 0,
    }
  }

  const pagePermission = getPagePermission()
  const showActionColumn =
    loginType === 'ADMIN' ||
    pagePermission.is_edit == 1 ||
    pagePermission.is_view == 1 ||
    pagePermission.is_delete == 1
  //============================================

  const [form] = Form.useForm()
  const [subCast, setSubCast] = useState([])
  const [castList, setCastList] = useState([])
  const [loading, setLoading] = useState(false)
  const [addLoading, setAddLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [deleteLoadingId, setDeleteLoadingId] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedDeleteId, setSelectedDeleteId] = useState(null)

  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [selectedSubCast, setSelectedSubCast] = useState(null)

  const handleView = (record) => {
    setSelectedSubCast(record)
    setViewModalVisible(true)
  }

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
  })

  //  Fetch all subcasts
  const fetchSubCast = async () => {
    try {
      setLoading(true)
      const res = await axiosInstance.get(getSubCast)
      setSubCast(res.data)
    } catch (err) {
      toast.error('Failed to fetch Subcast')
    } finally {
      setLoading(false)
    }
  }

  //  Fetch all casts for dropdown
  const fetchCastList = async () => {
    try {
      const res = await axiosInstance.get(getCast)
      setCastList(res.data)
    } catch (err) {
      toast.error('Failed to fetch Cast list')
    }
  }

  useEffect(() => {
    fetchCastList()
    fetchSubCast()
  }, [])

  //  Add or Update Subcast
  const onFinish = async (values) => {
    const { castId, subCastName } = values
    if (!castId || !subCastName) {
      toast.error('Please fill in all required fields')
      return
    }

    const saveObj = {
      cast_id: castId,
      subcast_name: subCastName,
    }
    const updateObj = {
      subcast_name: subCastName,
    }

    try {
      if (editingId) {
        setUpdateLoading(true)
        const res = await axiosInstance.put(
          `${updateSubCast}/${editingId}`,
          updateObj,
        )
        if (res.data === 'UPDATED') {
          toast.success('Subcast updated successfully')
          fetchSubCast()
        }
      } else {
        setAddLoading(true)
        const res = await axiosInstance.post(saveSubCast, saveObj)
        if (res.data === 'SAVED') {
          toast.success('Subcast added successfully')
          fetchSubCast()
        }
      }
      form.resetFields()
      setEditingId(null)
    } catch (err) {
      toast.error('Operation failed. Please try again.')
    } finally {
      setAddLoading(false)
      setUpdateLoading(false)
    }
  }

  //  Edit Subcast
  const handleEdit = (record) => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })

    setEditingId(record.id)
    form.setFieldsValue({
      castId: record.cast_id,
      subCastName: record.subcast_name,
    })
  }

  //  Delete Subcast
  const handleDelete = async () => {
    try {
      setDeleteLoadingId(selectedDeleteId)

      const res = await axiosInstance.delete(
        `${deleteSubCast}/${selectedDeleteId}`,
      )

      if (res.data === 'DELETED') {
        toast.success('Subcast deleted successfully')
        fetchSubCast()
        setIsDeleteModalOpen(false)
        setSelectedDeleteId(null)
      }
    } catch (err) {
      toast.error('Failed to delete Subcast')
    } finally {
      setDeleteLoadingId(null)
    }
  }

  //  Cancel Edit
  const handleCancel = () => {
    setEditingId(null)
    form.resetFields()
  }

  //  Table Columns
  const columns = [
    {
      title: 'Sr. No',
      key: 'index',
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
      width: '10%',
    },
    {
      title: 'Cast Name',
      render: (_, record) => record.castmaster?.cast_name || '-',
      key: 'cast_name',
    },
    {
      title: 'Subcast Name',
      dataIndex: 'subcast_name',
      key: 'subcast_name',
    },
    ...(showActionColumn
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: '25%',
            render: (_, record) => (
              <Space>
                {pagePermission.is_view == 1 && (
                  <Button
                    icon={<EyeOutlined />}
                    onClick={() => handleView(record)}
                  />
                )}
                {pagePermission.is_edit == 1 && (
                  <Button
                    title="Edit"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  ></Button>
                )}
                {pagePermission.is_delete == 1 && (
                  <Button
                    title="Delete"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setSelectedDeleteId(record.id)
                      setIsDeleteModalOpen(true)
                    }}
                  ></Button>
                )}
              </Space>
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      <AdminHeader />

      <div className="student-form-section container-fluid">
        <div className="student-form-wrapper pb-5">
          <div className="row student-form-title">
            <h3 className="section-title1 text-start mb-2">Subcast Master</h3>
          </div>

          <div className="row student-form-container shadow p-4 rounded bg-white">
            {/* Subcast Form */}
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              style={{ marginBottom: '20px' }}
            >
              <Row gutter={16} className="pt-3">
                {/* Cast Dropdown */}
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label="Cast"
                    name="castId"
                    rules={[{ required: true, message: 'Please select Cast' }]}
                  >
                    <Select
                      placeholder="Select Cast"
                      showSearch
                      optionFilterProp="children"
                    >
                      {castList.map((c) => (
                        <Select.Option key={c.id} value={c.id}>
                          {c.cast_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                {/* Subcast Input */}
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label="Subcast Name"
                    name="subCastName"
                    rules={[
                      { required: true, message: 'Please enter Subcast name' },
                    ]}
                  >
                    <Input placeholder="Enter Subcast" />
                  </Form.Item>
                </Col>

                {/* Buttons */}
                <Col
                  xs={24}
                  sm={12}
                  md={8}
                  style={{ display: 'flex', alignItems: 'end' }}
                >
                  <Form.Item>
                    <Space>
                      {!editingId ? (
                        <>
                          {pagePermission.is_new == 1 && (
                            <Button
                              type="primary"
                              htmlType="submit"
                              icon={<PlusOutlined />}
                              loading={addLoading}
                            >
                              Add
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          <Button
                            type="primary"
                            htmlType="submit"
                            icon={<EditOutlined />}
                            loading={updateLoading}
                          >
                            Update
                          </Button>
                          <Button
                            onClick={handleCancel}
                            disabled={updateLoading}
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            {/* Subcast Table */}
            <Table
              columns={columns}
              dataSource={subCast}
              rowKey="id"
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: subCast.length,
                onChange: (page, pageSize) => {
                  setPagination({
                    current: page,
                    pageSize: pageSize,
                  })
                },
              }}
              bordered
              loading={loading}
              className="shadow rounded custom-table p-0"
              style={{ overflowX: 'auto', border: '1px solid #dfdfdf' }}
            />
          </div>


         {/* ======== Delete Modal ====== */}
          <Modal
            title="Delete Subcast"
            open={isDeleteModalOpen}
            onCancel={() => {
              setIsDeleteModalOpen(false)
              setSelectedDeleteId(null)
            }}
            footer={[
              <Button
                key="cancel"
                onClick={() => {
                  setIsDeleteModalOpen(false)
                  setSelectedDeleteId(null)
                }}
              >
                Cancel
              </Button>,
              <Button
                key="delete"
                type="primary"
                danger
                loading={deleteLoadingId !== null}
                onClick={handleDelete}
              >
                Delete
              </Button>,
            ]}
          >
            <p>Are you sure you want to delete this Subcast?</p>
          </Modal>

          {/* ======== View Modal ====== */}
          <Modal
            title={
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                👁 View Subcast Details
              </div>
            }
            open={viewModalVisible}
            onCancel={() => setViewModalVisible(false)}
            footer={[
              <Button
                key="close"
                type="primary"
                onClick={() => setViewModalVisible(false)}
              >
                Close
              </Button>,
            ]}
            centered
          >
            <div
              style={{
                padding: '15px',
                borderRadius: '8px',
                backgroundColor: '#f5f7fa',
                border: '1px solid #e6e6e6',
              }}
            >
              <Row gutter={[16, 16]}>
                {/* Cast Name */}
                <Col span={8}>
                  <span style={{ fontWeight: 600 }}>Cast :</span>
                </Col>

                <Col span={16}>
                  <div
                    style={{
                      backgroundColor: '#ffffff',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                    }}
                  >
                    {selectedSubCast?.castmaster?.cast_name || '-'}
                  </div>
                </Col>

                {/* Subcast Name */}
                <Col span={8}>
                  <span style={{ fontWeight: 600 }}>Subcast Name :</span>
                </Col>

                <Col span={16}>
                  <div
                    style={{
                      backgroundColor: '#ffffff',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                    }}
                  >
                    {selectedSubCast?.subcast_name}
                  </div>
                </Col>
              </Row>
            </div>
          </Modal>

        </div>
      </div>

      <AdminFooter />
    </>
  )
}

export default SubCastMaster
